import React, { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SectionEditor from '@/components/SectionEditor';
import {
  Plus, Edit2, Trash2, Package, Search,
  Box, Gift, Mail, Truck, ShoppingBag, Shield,
  Square, Monitor, Layers, Eye, Upload
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const categoryIcons = {
  'Square Box': Square, 'Gift Box': Gift, 'Mailer Box': Mail, 'Shipping Box': Truck,
  'Product Box (Custom Design)': ShoppingBag, 'Food Box': Package, 'Display Box': Monitor,
  'Folding Box': Layers, 'Sleeve & Tray Box': Box, 'Boxes with Window': Monitor,
  'Boxes with Handle': Package, 'Boxes with Lid': Box, 'Hanging Box': Package,
  'Tuck End Box': Box, 'Polygonal Box': Box, 'Plastic Cover (Wrapper)': Layers,
  'Pizza Packaging Box': Package, 'Cake Box': Gift, 'Heavy Duty Boxes': Shield,
};

const defaultProductCategories = [
  { id: 1, name: 'Square Box', items: [], description: 'Classic square packaging' },
  { id: 2, name: 'Gift Box', items: [], description: 'Premium gift packaging' },
  { id: 3, name: 'Mailer Box', items: [], description: 'Shipping-ready mailer boxes' },
  { id: 4, name: 'Shipping Box', items: [], description: 'Heavy-duty shipping' },
  { id: 5, name: 'Product Box (Custom Design)', items: [], description: 'Fully customizable' },
  { id: 6, name: 'Food Box', items: [], description: 'Food-grade packaging' },
  { id: 7, name: 'Display Box', items: [], description: 'Retail display packaging' },
  { id: 8, name: 'Folding Box', items: [], description: 'Space-saving folding' },
  { id: 9, name: 'Sleeve & Tray Box', items: [], description: 'Two-piece system' },
  { id: 10, name: 'Boxes with Window', items: [], description: 'Transparent window' },
  { id: 11, name: 'Boxes with Handle', items: [], description: 'Convenient carry' },
  { id: 12, name: 'Boxes with Lid', items: [], description: 'Lidded solutions' },
  { id: 13, name: 'Hanging Box', items: [], description: 'Retail hanging' },
  { id: 14, name: 'Tuck End Box', items: [], description: 'Auto-lock tuck end' },
  { id: 15, name: 'Polygonal Box', items: [], description: 'Unique geometric' },
  { id: 16, name: 'Plastic Cover (Wrapper)', items: [], description: 'Protective wrapping' },
  { id: 17, name: 'Pizza Packaging Box', items: [], description: 'Specialized pizza' },
  { id: 18, name: 'Cake Box', items: [], description: 'Bakery and cake' },
  { id: 19, name: 'Heavy Duty Boxes', items: [], description: 'Industrial strength' },
];

export default function ProductsPage({ user, setUser, theme, toggleTheme, accentColor, onAccentChange }) {
  const navigate = useNavigate();
  const [categories, setCategories] = useState(defaultProductCategories);
  const [selectedCatId, setSelectedCatId] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [showItemDialog, setShowItemDialog] = useState(false);
  const [itemForm, setItemForm] = useState({ name: '', description: '', image: '', video: '', specifications: '' });
  const [editingItem, setEditingItem] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [imageInputMode, setImageInputMode] = useState('url');
  const [videoInputMode, setVideoInputMode] = useState('url');

  const loadProductData = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/pages/products`);
      if (response.data?.content?.categories) setCategories(response.data.content.categories);
    } catch (error) { console.error('Failed to load products:', error); }
  }, []);

  useEffect(() => { loadProductData(); }, [loadProductData]);

  const saveProductData = async (newCategories) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/pages/products`, { content: { categories: newCategories } }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Products updated!');
    } catch (error) { toast.error('Failed to save'); }
  };

  const selectedCategory = categories.find(c => c.id === selectedCatId) || categories[0];
  const filteredCategories = categories.filter(c => c.name.toLowerCase().includes(searchQuery.toLowerCase()));

  const openAddItem = () => {
    setItemForm({ name: '', description: '', image: '', video: '', specifications: '' });
    setEditingItem(null);
    setShowItemDialog(true);
  };

  const openEditItem = (item, index) => {
    setItemForm(item);
    setEditingItem(index);
    setShowItemDialog(true);
  };

  const handleSaveItem = () => {
    if (!itemForm.name.trim()) { toast.error('Enter item name'); return; }
    const updated = categories.map(cat => {
      if (cat.id === selectedCatId) {
        const items = [...cat.items];
        if (editingItem !== null) items[editingItem] = itemForm;
        else items.push(itemForm);
        return { ...cat, items };
      }
      return cat;
    });
    setCategories(updated);
    saveProductData(updated);
    setShowItemDialog(false);
  };

  const handleDeleteItem = (index) => {
    const updated = categories.map(cat => {
      if (cat.id === selectedCatId) return { ...cat, items: cat.items.filter((_, i) => i !== index) };
      return cat;
    });
    setCategories(updated);
    saveProductData(updated);
  };

  const handleFileUpload = async (event, type) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const setter = type === 'image' ? setUploadingImage : setUploadingVideo;
    setter(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) { toast.error('Login as admin'); setter(false); return; }
      const fd = new FormData(); fd.append('file', file);
      const res = await axios.post(`${API}/upload`, fd, { headers: { Authorization: `Bearer ${token}` } });
      setItemForm(prev => ({ ...prev, [type]: res.data.url.startsWith('http') ? res.data.url : `${BACKEND_URL}${res.data.url}` }));
      toast.success(`${type} uploaded!`);
    } catch (error) { toast.error('Upload failed'); }
    finally { setter(false); }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar user={user} setUser={setUser} theme={theme} toggleTheme={toggleTheme} accentColor={accentColor} onAccentChange={onAccentChange} />

      {/* Header */}
      <section className="relative py-10 overflow-hidden">
        <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, hsl(var(--primary) / 0.05) 0%, transparent 100%)' }}></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary-end)))' }}>
                <Package className="w-7 h-7 text-primary-foreground" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-black text-foreground tracking-tight" data-testid="products-title">Products</h1>
                <p className="text-sm text-muted-foreground">{categories.reduce((a, c) => a + c.items.length, 0)} items across {categories.length} categories</p>
              </div>
            </div>
            <div className="w-full sm:w-64">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="text" placeholder="Search categories..."
                  value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-card border-border/30 text-foreground rounded-xl"
                  data-testid="product-search"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Layout: Sidebar + Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-col md:flex-row gap-4 md:gap-6" style={{ minHeight: 'min(calc(100vh - 220px), 800px)' }}>
          {/* Left Sidebar - All Categories */}
          <div className="w-full md:w-72 md:flex-shrink-0 max-h-[40vh] md:max-h-none md:h-[calc(100vh-220px)] overflow-y-auto rounded-xl border border-border/20 bg-card/50" data-testid="category-sidebar">
            <div className="p-3 border-b border-border/20 sticky top-0 bg-card/80 backdrop-blur-sm z-10">
              <p className="text-xs font-bold text-muted-foreground tracking-widest font-mono-spec">CATEGORIES ({filteredCategories.length})</p>
            </div>
            <div className="p-2 space-y-0.5">
              {filteredCategories.map((cat) => {
                const Icon = categoryIcons[cat.name] || Package;
                const isActive = cat.id === selectedCatId;
                return (
                  <button
                    key={cat.id}
                    onClick={() => setSelectedCatId(cat.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all text-sm ${
                      isActive
                        ? 'bg-primary/10 text-primary border border-primary/20'
                        : 'text-foreground/70 hover:bg-muted/50 border border-transparent'
                    }`}
                    data-testid={`category-btn-${cat.id}`}
                  >
                    <Icon className={`w-4 h-4 flex-shrink-0 ${isActive ? 'text-primary' : 'text-muted-foreground'}`} />
                    <span className="flex-1 truncate font-medium">{cat.name}</span>
                    {cat.items.length > 0 && (
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold font-mono-spec ${isActive ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
                        {cat.items.length}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Right Content - Selected Category */}
          <div className="flex-1 overflow-y-auto rounded-xl border border-border/20 bg-card/30 min-h-[50vh] md:min-h-0">
            {/* Category Header */}
            <div className="sticky top-0 z-10 p-5 border-b border-border/20 bg-card/80 backdrop-blur-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {React.createElement(categoryIcons[selectedCategory.name] || Package, { className: "w-6 h-6 text-primary" })}
                  <div>
                    <h2 className="text-xl font-black text-foreground tracking-tight">{selectedCategory.name}</h2>
                    <p className="text-xs text-muted-foreground">{selectedCategory.description} &middot; {selectedCategory.items.length} items</p>
                  </div>
                </div>
                {user?.role === 'admin' && (
                  <Button onClick={openAddItem} size="sm" className="rounded-xl font-bold text-primary-foreground" style={{ background: 'linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary-end)))' }} data-testid="add-item-btn">
                    <Plus className="w-4 h-4 mr-2" /> Add Item
                  </Button>
                )}
              </div>
            </div>

            {/* Items Grid */}
            <div className="p-5">
              <AnimatePresence mode="wait">
                {selectedCategory.items.length === 0 ? (
                  <motion.div key="empty" className="text-center py-20" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                    {React.createElement(categoryIcons[selectedCategory.name] || Package, { className: "w-16 h-16 text-muted-foreground/30 mx-auto mb-4" })}
                    <p className="text-lg font-semibold text-foreground/50 mb-2">No items in {selectedCategory.name}</p>
                    <p className="text-sm text-muted-foreground mb-6">This category is empty{user?.role === 'admin' ? '. Add your first product.' : '.'}</p>
                    {user?.role === 'admin' && (
                      <Button onClick={openAddItem} size="sm" variant="outline" className="rounded-xl border-border/30">
                        <Plus className="w-4 h-4 mr-2" /> Add First Item
                      </Button>
                    )}
                  </motion.div>
                ) : (
                  <motion.div key="items" className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                    {selectedCategory.items.map((item, idx) => (
                      <motion.div key={idx} className="elegant-card overflow-hidden group" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.05 }}>
                        {item.image && (
                          <div className="h-40 overflow-hidden">
                            <img src={item.image} alt={item.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                          </div>
                        )}
                        {!item.image && item.video && (
                          <div className="h-40 bg-black overflow-hidden">
                            <video src={item.video} className="w-full h-full object-cover" muted />
                          </div>
                        )}
                        <div className="p-4">
                          <h3 className="font-bold text-foreground mb-1 truncate">{item.name}</h3>
                          {item.description && <p className="text-xs text-muted-foreground mb-2 line-clamp-2">{item.description}</p>}
                          {item.specifications && <p className="text-[10px] text-muted-foreground font-mono-spec mb-3">{item.specifications}</p>}
                          <div className="flex items-center gap-2">
                            <Button size="sm" variant="outline" onClick={() => navigate('/products/view', { state: { product: item, categoryName: selectedCategory.name } })} className="flex-1 text-xs rounded-lg border-border/30 hover:border-primary/30" data-testid={`view-btn-${idx}`}>
                              <Eye className="w-3 h-3 mr-1" /> View & Quote
                            </Button>
                            {user?.role === 'admin' && (
                              <>
                                <Button size="sm" variant="ghost" onClick={() => openEditItem(item, idx)} className="h-8 w-8 p-0"><Edit2 className="w-3.5 h-3.5 text-muted-foreground" /></Button>
                                <Button size="sm" variant="ghost" onClick={() => handleDeleteItem(idx)} className="h-8 w-8 p-0 text-destructive"><Trash2 className="w-3.5 h-3.5" /></Button>
                              </>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>

      <Footer user={user} setUser={setUser} />

      {/* Add/Edit Item Dialog */}
      <Dialog open={showItemDialog} onOpenChange={setShowItemDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col bg-card border-border/30">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-foreground">
              {React.createElement(categoryIcons[selectedCategory?.name] || Package, { className: "w-5 h-5 text-primary" })}
              {editingItem !== null ? 'Edit Item' : 'Add New Item'} - {selectedCategory?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 overflow-y-auto flex-1 px-1">
            <div>
              <label className="text-sm font-medium text-foreground mb-1 block">Item Name *</label>
              <Input value={itemForm.name} onChange={(e) => setItemForm({...itemForm, name: e.target.value})} placeholder="e.g., Premium Gift Box" className="bg-background border-border/30 text-foreground" />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground mb-1 block">Description</label>
              <Textarea value={itemForm.description} onChange={(e) => setItemForm({...itemForm, description: e.target.value})} placeholder="Brief description" rows={2} className="bg-background border-border/30 text-foreground" />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">Image</label>
              <div className="flex gap-2 mb-2">
                <Button type="button" size="sm" variant={imageInputMode === 'url' ? 'default' : 'outline'} onClick={() => setImageInputMode('url')} className={imageInputMode === 'url' ? 'bg-primary text-primary-foreground' : 'border-border/30'}>URL</Button>
                <Button type="button" size="sm" variant={imageInputMode === 'upload' ? 'default' : 'outline'} onClick={() => setImageInputMode('upload')} className={imageInputMode === 'upload' ? 'bg-primary text-primary-foreground' : 'border-border/30'}>Upload</Button>
              </div>
              {imageInputMode === 'url' && <Input value={itemForm.image} onChange={(e) => setItemForm({...itemForm, image: e.target.value})} placeholder="https://..." className="bg-background border-border/30 text-foreground" />}
              {imageInputMode === 'upload' && (
                <div className="border-2 border-dashed border-border/30 rounded-xl p-4 text-center hover:border-primary/40 transition-colors">
                  <input type="file" accept="image/*" onChange={(e) => handleFileUpload(e, 'image')} className="hidden" id="img-up" disabled={uploadingImage} />
                  <label htmlFor="img-up" className="cursor-pointer flex items-center justify-center gap-2 text-sm text-muted-foreground">
                    {uploadingImage ? <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin"></div> : <Upload className="w-5 h-5" />}
                    {uploadingImage ? 'Uploading...' : 'Click to upload'}
                  </label>
                </div>
              )}
              {itemForm.image && <div className="mt-2"><img src={itemForm.image} alt="Preview" className="w-full h-32 object-cover rounded-lg border border-border/30" /><Button size="sm" variant="outline" onClick={() => setItemForm({...itemForm, image: ''})} className="mt-1 text-destructive border-border/30 w-full">Remove</Button></div>}
            </div>
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">Video (optional)</label>
              <div className="flex gap-2 mb-2">
                <Button type="button" size="sm" variant={videoInputMode === 'url' ? 'default' : 'outline'} onClick={() => setVideoInputMode('url')} className={videoInputMode === 'url' ? 'bg-primary text-primary-foreground' : 'border-border/30'}>URL</Button>
                <Button type="button" size="sm" variant={videoInputMode === 'upload' ? 'default' : 'outline'} onClick={() => setVideoInputMode('upload')} className={videoInputMode === 'upload' ? 'bg-primary text-primary-foreground' : 'border-border/30'}>Upload</Button>
              </div>
              {videoInputMode === 'url' && <Input value={itemForm.video} onChange={(e) => setItemForm({...itemForm, video: e.target.value})} placeholder="https://..." className="bg-background border-border/30 text-foreground" />}
              {videoInputMode === 'upload' && (
                <div className="border-2 border-dashed border-border/30 rounded-xl p-4 text-center hover:border-primary/40 transition-colors">
                  <input type="file" accept="video/*" onChange={(e) => handleFileUpload(e, 'video')} className="hidden" id="vid-up" disabled={uploadingVideo} />
                  <label htmlFor="vid-up" className="cursor-pointer flex items-center justify-center gap-2 text-sm text-muted-foreground">
                    {uploadingVideo ? <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin"></div> : <Upload className="w-5 h-5" />}
                    {uploadingVideo ? 'Uploading...' : 'Click to upload'}
                  </label>
                </div>
              )}
              {itemForm.video && <div className="mt-2"><video src={itemForm.video} controls className="w-full h-32 object-cover rounded-lg bg-black border border-border/30" /><Button size="sm" variant="outline" onClick={() => setItemForm({...itemForm, video: ''})} className="mt-1 text-destructive border-border/30 w-full">Remove</Button></div>}
            </div>
            <div>
              <label className="text-sm font-medium text-foreground mb-1 block">Specifications</label>
              <Input value={itemForm.specifications} onChange={(e) => setItemForm({...itemForm, specifications: e.target.value})} placeholder="e.g., Size: 10x10x5 cm" className="bg-background border-border/30 text-foreground font-mono-spec" />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-4 border-t border-border/30">
            <Button variant="outline" onClick={() => setShowItemDialog(false)} className="border-border/30 rounded-xl">Cancel</Button>
            <Button onClick={handleSaveItem} className="rounded-xl font-bold text-primary-foreground" style={{ background: 'linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary-end)))' }}>{editingItem !== null ? 'Update' : 'Add Item'}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
